import { useUser } from '../context/UserContext';

export interface PermissionCheckResult {
  hasAccess: boolean;
  reason?: string;
}

export const usePermissions = () => {
  const { user } = useUser();

  const isAdmin = () => user?.rol === 'ADMIN';
  const isAsociado = () => user?.rol === 'ASOCIADO';
  const isAfiliado = () => user?.rol === 'AFILIADO';
  const isPublic = () => user?.rol === 'PUBLICO' || !user;

  // Sprint 10 (F6-003/004/005): chequea modulos_acceso del admin_comite
  const hasModulo = (modulo: string): boolean => {
    if (!user?.esAdminComite) return false;
    const modulos = (user as any).modulosAcceso;
    return Array.isArray(modulos) && modulos.includes(modulo);
  };

  /**
   * Sprint 2 (F5E-008): permiso fino para herramientas destructivas.
   * Lee `permisos_destructivos: string[]` del doc admin del usuario.
   * Super admin (user.permisos_destructivos incluye '*' o 'all') tiene todos.
   */
  const hasDestructivePerm = (perm: string): boolean => {
    if (!user) return false;
    const list = (user as any).permisos_destructivos;
    if (!Array.isArray(list)) return false;
    return list.includes('*') || list.includes('all') || list.includes(perm);
  };

  // Verificar si el usuario puede gestionar una propiedad específica
  const canManageProperty = (propertyData?: any): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };
    
    if (isAdmin()) return { hasAccess: true };
    
    // Sprint 9 (F4-009): normalizar a inmobiliaria_doc_id; fallback a legacy inmobiliaria_id.
    const userInmoId = (user as any).inmobiliaria_doc_id || (user as any).inmobiliaria_id;
    const propInmoId = propertyData?.inmobiliaria_doc_id || propertyData?.inmobiliaria_id;

    if (isAsociado() && propInmoId) {
      const hasAccess = userInmoId === propInmoId;
      return {
        hasAccess,
        reason: hasAccess ? undefined : 'Property belongs to different agency'
      };
    }

    if (isAfiliado() && propertyData?.id) {
      // Sprint 9 (F3-009): validar array antes de includes.
      const propiedadIds = (user as any).propiedad_doc_ids || (user as any).propiedad_id || [];
      const hasAccess = Array.isArray(propiedadIds) && propiedadIds.includes(propertyData.id);
      return {
        hasAccess,
        reason: hasAccess ? undefined : 'Property not assigned to this affiliate'
      };
    }
    
    return { hasAccess: false, reason: 'Insufficient permissions' };
  };

  // Verificar si el usuario puede gestionar una inmobiliaria específica
  const canManageAgency = (agencyId?: string): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };
    
    if (isAdmin()) return { hasAccess: true };
    
    if (isAsociado() && agencyId) {
      const userInmoId = (user as any).inmobiliaria_doc_id || (user as any).inmobiliaria_id;
      const hasAccess = userInmoId === agencyId;
      return { 
        hasAccess, 
        reason: hasAccess ? undefined : 'Can only manage own agency' 
      };
    }
    
    return { hasAccess: false, reason: 'Only admin and associated users can manage agencies' };
  };

  // Verificar si el usuario puede gestionar un proyecto específico
  const canManageProject = (projectData?: any): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };
    
    if (isAdmin()) return { hasAccess: true };
    
    if (isAsociado() && (projectData?.inmobiliaria_doc_id || projectData?.inmobiliaria_id)) {
      const userInmoId = (user as any).inmobiliaria_doc_id || (user as any).inmobiliaria_id;
      const projInmoId = projectData?.inmobiliaria_doc_id || projectData?.inmobiliaria_id;
      const hasAccess = userInmoId === projInmoId;
      return { 
        hasAccess, 
        reason: hasAccess ? undefined : 'Project belongs to different agency' 
      };
    }
    
    return { hasAccess: false, reason: 'Only admin and associated users can manage projects' };
  };

  // Verificar permisos por categoría de acción
  const checkPermission = (action: string, resourceData?: any): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };

    // Sprint 5 (F2-013): bloquear si el usuario fue desactivado (activo===false).
    // ADMIN general no tiene flag `activo` en su doc — se exceptúa.
    // Acciones de solo-lectura públicas se permiten ("view-*") para evitar bloquear navegación básica.
    if (user.rol !== 'ADMIN' && (user as any).activo === false && !action.startsWith('view-')) {
      return { hasAccess: false, reason: 'Usuario desactivado — contacta al administrador' };
    }

    const permissions: Record<string, () => PermissionCheckResult> = {
      // Solo ADMIN
      'manage-all-properties': () => ({ hasAccess: isAdmin() }),
      'manage-all-agencies': () => ({ hasAccess: isAdmin() }),
      'manage-all-projects': () => ({ hasAccess: isAdmin() }),
      'manage-all-affiliates': () => ({ hasAccess: isAdmin() }),
      'manage-all-associates': () => ({ hasAccess: isAdmin() }),
      'approve-properties': () => ({ hasAccess: isAdmin() }),
      'approve-reviews': () => ({ hasAccess: isAdmin() }),
      // Sprint 10 (F6-003/004/005): admin_comite con modulo 'talleres' tambien permite.
      'manage-all-courses': () => ({ hasAccess: isAdmin() || hasModulo('talleres') }),
      'create-course': () => ({ hasAccess: isAdmin() || hasModulo('talleres') }),
      'edit-course': () => ({ hasAccess: isAdmin() || hasModulo('talleres') }),
      'manage-attendance': () => ({ hasAccess: isAdmin() || hasModulo('talleres') }),
      'scan-attendance': () => ({ hasAccess: isAdmin() || hasModulo('talleres') }),
      'create-associate': () => ({ hasAccess: isAdmin() }),
      'manage-assignments': () => ({ hasAccess: isAdmin() }),

      // Sprint 2 (F5E-008): permisos finos para herramientas destructivas.
      // Actualmente todos requieren ADMIN con flag opt-in en admin.permisos_destructivos.
      // Ejemplo en Firestore doc admin/:uid => { permisos_destructivos: ['cleanup','migrate'] }
      'tool:database-cleanup': () => ({ hasAccess: isAdmin() && hasDestructivePerm('cleanup') }),
      'tool:migrate-members': () => ({ hasAccess: isAdmin() && hasDestructivePerm('migrate') }),
      'tool:reconcile': () => ({ hasAccess: isAdmin() && hasDestructivePerm('reconcile') }),
      'tool:relocate-members': () => ({ hasAccess: isAdmin() && hasDestructivePerm('relocate') }),
      'tool:limpiar-duplicados': () => ({ hasAccess: isAdmin() && hasDestructivePerm('cleanup') }),
      'tool:bulk-password-reset': () => ({ hasAccess: isAdmin() && hasDestructivePerm('password_reset') }),
      'tool:mls-sync-trigger': () => ({ hasAccess: isAdmin() && hasDestructivePerm('mls') }),

      // ADMIN + ASOCIADO
      'create-affiliate': () => ({ hasAccess: isAdmin() || isAsociado() }),
      'create-property': () => ({ hasAccess: isAdmin() || isAsociado() }),
      'edit-agency': () => canManageAgency(resourceData?.id || resourceData?.inmobiliaria_id),

      // ADMIN + ASOCIADO + AFILIADO
      'manage-property': () => canManageProperty(resourceData),
      'edit-property': () => canManageProperty(resourceData),
      'access-dashboard': () => ({ hasAccess: !isPublic() }),

      // ASOCIADO + AFILIADO
      'edit-profile': () => ({ hasAccess: isAsociado() || isAfiliado() }),
      'view-my-properties': () => ({ hasAccess: isAsociado() || isAfiliado() }),
      'view-my-projects': () => ({ hasAccess: isAsociado() || isAfiliado() }),
      'view-project': () => canManageProject(resourceData),

      // Solo ASOCIADO
      'edit-projects': () => canManageProject(resourceData),

      // Públicas (siempre permitidas)
      'view-agencies': () => ({ hasAccess: true }),
      'view-agency': () => ({ hasAccess: true }),
      'view-affiliates': () => ({ hasAccess: true }),
      'view-affiliate': () => ({ hasAccess: true }),
      'view-associates': () => ({ hasAccess: true }),
      'view-associate': () => ({ hasAccess: true }),
      'view-properties': () => ({ hasAccess: true }),
      'view-property': () => ({ hasAccess: true }),
      'add-review': () => ({ hasAccess: true }),
      'view-courses': () => ({ hasAccess: true }),
      'enroll-course': () => ({ hasAccess: true }),
      'view-course': () => ({ hasAccess: true })
    };

    const permissionCheck = permissions[action];
    if (!permissionCheck) {
      return { hasAccess: false, reason: `Unknown action: ${action}` };
    }

    return permissionCheck();
  };

  return {
    user,
    isAdmin,
    isAsociado,
    isAfiliado,
    isPublic,
    hasDestructivePerm,
    canManageProperty,
    canManageAgency,
    canManageProject,
    checkPermission
  };
};

/**
 * Hook para verificar permisos específicos de páginas según las reglas establecidas
 */
export const usePagePermissions = () => {
  const { isAdmin, isAsociado, isAfiliado, canManageProperty, canManageAgency, canManageProject } = usePermissions();

  const pagePermissions = {
    // Solo ADMIN
    adminDashboard: () => isAdmin(),
    manageAllProperties: () => isAdmin(),
    manageAllAgencies: () => isAdmin(), 
    manageAllProjects: () => isAdmin(),
    manageAllAffiliates: () => isAdmin(),
    manageAllAssociates: () => isAdmin(),
    approveProperties: () => isAdmin(),
    approveReviews: () => isAdmin(),
    manageAllCourses: () => isAdmin(),
    createCourse: () => isAdmin(),
    editCourse: () => isAdmin(),
    manageAttendance: () => isAdmin(),
    scanAttendance: () => isAdmin(),
    createAssociate: () => isAdmin(),
    
    // ADMIN + ASOCIADO
    createAffiliate: () => isAdmin() || isAsociado(),
    createProperty: () => isAdmin() || isAsociado(),
    editAgency: (agencyId?: string) => canManageAgency(agencyId).hasAccess,
    
    // ADMIN + ASOCIADO + AFILIADO
    manageDashboard: () => isAdmin() || isAsociado() || isAfiliado(),
    editProperty: (propertyData?: any) => canManageProperty(propertyData).hasAccess,
    
    // ASOCIADO + AFILIADO  
    editProfile: () => isAsociado() || isAfiliado(),
    viewMyProperties: () => isAsociado() || isAfiliado(),
    viewMyProjects: () => isAsociado() || isAfiliado(),
    viewProject: (projectData?: any) => canManageProject(projectData).hasAccess,
    
    // Solo ASOCIADO
    editProjects: (projectData?: any) => isAsociado() && canManageProject(projectData).hasAccess,
    
    // Siempre públicas
    viewAgencies: () => true,
    viewAgency: () => true,
    viewAffiliates: () => true,
    viewAffiliate: () => true,
    viewAssociates: () => true,
    viewAssociate: () => true,
    viewProperties: () => true,
    viewProperty: () => true,
    addReview: () => true,
    viewCourses: () => true,
    enrollCourse: () => true,
    viewCourse: () => true
  };

  return pagePermissions;
};